import React, { useState, useEffect } from 'react';
import {
  Table,
  Select,
  Button,
  Space,
  InputNumber,
  message,
} from 'antd';
import { apiRequest } from '../../../service/apiRequest';
import { showSuccess, showError } from '../../../components/errorHandler';


const { Option } = Select;

interface Tailor {
  ledger_id: number;
  ledger_name?: string;
}

interface TailorPrice {
  tp_price: number;
  product_id: number;
  product_name: string;
  variant_code: string;
  variant_name: string;
}

const TailorPriceManager: React.FC = () => {
  const [tailors, setTailors] = useState<Tailor[]>([]);
  const [selectedTailorId, setSelectedTailorId] = useState<number | undefined>(undefined);
  const [tailorPrices, setTailorPrices] = useState<TailorPrice[]>([]);
  const [updatedPrices, setUpdatedPrices] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);


  // Fetch tailors on mount
  useEffect(() => {
    async function fetchTailors() {
      try {
        const data = await apiRequest({ method: 'get', url: '/ledger-data' });
        console.log("data", data)
        setTailors(
          data.ledgers.map((t: any) => ({
            ledger_id: t.ledger_id,
            ledger_name: t.ledger_first_name + ' ' + t.ledger_last_name,
          }))
        );
      } catch {
        message.error('Failed to load tailors');
      }
    }
    fetchTailors();
  }, []);

  // Fetch products and prices when tailor is selected
  useEffect(() => {
    if (!selectedTailorId) {
      setTailorPrices([]);
      setUpdatedPrices({});
      return;
    }

    async function fetchProductsAndPrices() {
      setLoading(true);
      try {
        const pricesData = await apiRequest<TailorPrice[]>({ method: 'get', url: `/tailor-price?ledger_id=${selectedTailorId}` });

        setTailorPrices(pricesData);

        // Initialize updatedPrices map
        const priceMap: Record<string, number> = {};
        pricesData.forEach((p) => {
          priceMap[p.variant_code] = p.tp_price && p.tp_price > 0 ? p.tp_price : 0;
        });

        setUpdatedPrices(priceMap);
      } catch {
        message.error('Failed to load products or prices');
      }
      setLoading(false);
    }
    fetchProductsAndPrices();
  }, [selectedTailorId]);


  // Handle price input change
  const handlePriceChange = (variant_code: string, value: number | null) => {
    setUpdatedPrices((prev) => ({ ...prev, [variant_code]: value ?? 0 }));
  };

  // Update prices API call
  const handleUpdatePrices = async () => {
    if (!selectedTailorId) {
      message.error('Please select a tailor first');
      return;
    }
    // setLoading(true);
    try {
      const payload = Object.entries(updatedPrices).map(([variant_code, price]) => ({
        tp_ledger_id: selectedTailorId,
        variant_code,     // <-- use variant_code instead
        tp_price: price,
      }));

      await apiRequest({
        method: 'post',
        url: '/tailor-price',
        data: payload,
      });
      showSuccess('Prices updated successfully');

    } catch {
      showError('Failed to update prices');
    }
    // setLoading(false);
  };


  const handleExportPDF = async () => {
    if (!selectedTailorId) {
      message.error('Please select a tailor first');
      return;
    }

    const selectedTailor = tailors.find((t) => t.ledger_id === selectedTailorId);
    const currentDate = new Date().toLocaleDateString();

    const productData = tailorPrices.map((p) => ({
      product_name: p.product_name,
      variant_code: p.variant_code || '-',
      variant_name: p.variant_name || '-',
      price: updatedPrices[p.variant_code] || 0,
    }));

    try {
      const response = await apiRequest({
        method: 'post',
        url: '/download-tailor-price',
        data: {
          tailorName: selectedTailor?.ledger_name,
          date: currentDate,
          products: productData,
        },
        config: {
          responseType: 'blob', // ✅ यहाँ पास कर सकते हैं
        },// 👈 important for PDF binary data
      });

      // Create a blob URL for download
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `Tailor_Price_${selectedTailor?.ledger_name}.pdf`;
      link.click();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      showError('Failed to export PDF');
      console.error(err);
    }
  };


  const columns = [
    {
      title: 'Product Name',
      dataIndex: 'product_name',
      key: 'product_name',
    },
    {
      title: 'Variant Code',
      dataIndex: 'variant_code',
      key: 'variant_code',
    },
    {
      title: 'Variant Name',
      dataIndex: 'variant_name',
      key: 'variant_name',
    },
    {
      title: 'Price',
      dataIndex: 'product_id',
      key: 'price',
      // render: (productId: number) => (
      //   <InputNumber
      //     min={0}
      //     value={updatedPrices[productId]}
      //     onChange={(value) => handlePriceChange(productId, value)}
      //     disabled={loading}
      //   />
      render: (_: any, record: TailorPrice) => (
        <InputNumber
          min={0}
          max={10000}
          maxLength={5}
          value={updatedPrices[record.variant_code]}
          onChange={(value) => handlePriceChange(record.variant_code, value)}
          disabled={loading}
          style={{ width: '50%' }}
        />
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Select
          placeholder="Select Tailor"
          style={{ width: 240 }}
          onChange={setSelectedTailorId}
          allowClear
          value={selectedTailorId}
          loading={loading}
          showSearch
          optionFilterProp="children"
        >
          {tailors.map((t) => (
            <Option key={t.ledger_id} value={t.ledger_id}>
              {t.ledger_name}
            </Option>
          ))}
        </Select>
        <Button type="primary" onClick={handleUpdatePrices}>
          Update Prices
        </Button>

        <Button onClick={handleExportPDF} type="default"
          disabled={!selectedTailorId} >
          Download Tailor Price
        </Button>
      </Space>


      <Table
        columns={columns}
        dataSource={tailorPrices}
        rowKey="variant_code"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />
    </div>
  );
};

export default TailorPriceManager;
